import { CircularProgress } from '@mui/material';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import useTheme from '@mui/material/styles/useTheme';
import useMediaQuery from '@mui/material/useMediaQuery';
import { arrayUnion, doc, getDoc, setDoc } from 'firebase/firestore';
import { FC, Fragment, useEffect, useState, } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { UserSkill, UserStatus } from '../../../consts/types/user';
import { db } from '../../../firebase';
import { UserSelectors, userActionsCreators } from '../../../reducers/user';
const { NotificationManager } = require('react-notifications');

const filter = createFilterOptions<{ inputValue?: string, title: string }>();

interface AddSkillDialogProps {
    setIsAddingSkills: (isAdding: boolean) => void;
    setIsSavingSkills: (isSaving: boolean) => void;
    isAddingSkill: boolean;
    isSavingSkills: boolean;
}

type Props = AddSkillDialogProps;


const AddSkillDialog: FC<Props> = (props: Props) => {
    const { isAddingSkill, isSavingSkills, setIsAddingSkills, setIsSavingSkills } = props;
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
    const [newSkill, setNewSkill] = useState<UserSkill>({ status: UserStatus.Pending } as any);
    const [skillsListOpen, setSkillsListOpen] = useState(false);
    const [skillsOptions, setSkillsOptions] = useState<{ inputValue?: string, title: string }[]>([]);
    const skillsLoading = skillsListOpen && skillsOptions.length === 0;

    const user = useSelector(UserSelectors.currentUser);
    const [skills, setSkills] = useState(user?.skills || []);
    const dispatch = useDispatch();
    
    useEffect(() => {
        setSkills(user?.skills || []);
    }, [user?.skills]);

    useEffect(() => {
        if (!skillsLoading) {
            return undefined;
        }
        getDoc(doc(db, "staticData", "skills"))
            .then(docSnap => {
                if (docSnap.exists()) {
                    setSkillsOptions(Object.keys(docSnap.data()).map(skill => ({
                        inputValue: skill,
                        title: skill
                    })))
                }
            })

    }, [skillsLoading]);

    useEffect(() => {
        if (!skillsListOpen) {
            setSkillsOptions([]);
        }
    }, [skillsListOpen]);

    const clearSkill = () => {
        setNewSkill({ status: UserStatus.Pending } as any);
    }

    const addSkill = () => {
        if (user?.email && newSkill.skill /*&& newSkill.level*/ && newSkill.years) {
            setIsSavingSkills(true);
            setIsAddingSkills(false);

            if (user) {
                dispatch(userActionsCreators.setUser({ user: { ...user, skills: [...skills, newSkill] } }));
            }
            setDoc(doc(db, "users", user.email), {
                skills: arrayUnion(newSkill),
                skillsNames: arrayUnion(newSkill.skill),
            }, { merge: true })
                .then((result) => {
                    setIsSavingSkills(false);
                    // setNewSkill(null);
                    // NotificationManager.success('Skill was added successfully!',);
                })
                .catch(() => {
                    setIsSavingSkills(false);
                    // setNewSkill(null);
                    NotificationManager.error('Something went wrong. Please try again.',);
                });
            clearSkill();
        }
    }

    return <Dialog fullScreen={fullScreen} fullWidth onClose={() => { setIsAddingSkills(false) }} open={isAddingSkill}>
        <DialogTitle>Add Skill</DialogTitle>
        <Box px={2} minWidth="30%">
            <Stack spacing={3}>
                <Autocomplete value={{ inputValue: newSkill.skill || "", title: newSkill.skill || "" }}
                    size="small"
                    open={skillsListOpen}
                    onOpen={() => {
                        setSkillsListOpen(true);
                    }}
                    onClose={() => {
                        setSkillsListOpen(false);
                    }}
                    onChange={(event, newValue, reason, details) => {
                        if (newValue && newValue.inputValue) {
                            // Create a new value from the user input
                            if (!skillsOptions.find(opt => opt.inputValue === newValue.inputValue)) {
                                setDoc(doc(db, "staticData", "skills"), {
                                    [newValue.inputValue]: newValue.inputValue
                                }, { merge: true })
                            }
                            setNewSkill({ ...newSkill, skill: newValue.inputValue });
                        }
                        else {
                            setNewSkill({ ...newSkill, skill: "" });
                        }

                    }}
                    filterOptions={(options, params) => {
                        const filtered = filter(options.filter(option => !skills.find(skill => skill.skill === option.inputValue)), params);

                        const { inputValue } = params;
                        // Suggest the creation of a new value
                        const isExisting = options.some((option) => inputValue === option.title);
                        if (inputValue !== '' && !isExisting) {
                            filtered.push({
                                inputValue,
                                title: `Add "${inputValue}"`,
                            });
                        }

                        return filtered;
                    }}
                    getOptionLabel={(option) => {
                        // Value selected with enter, right from the input
                        if (typeof option === 'string') {
                            return option;
                        }
                        // Add "xxx" option created dynamically
                        if (option.inputValue) {
                            return option.inputValue;
                        }
                        // Regular option
                        return option.title;
                    }}
                    options={skillsOptions}
                    loading={skillsLoading}
                    renderOption={(props, option) => <li {...props}>{option.title}</li>}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Skill"
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <Fragment>
                                        {skillsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                        {params.InputProps.endAdornment}
                                    </Fragment>
                                ),
                            }}
                        />
                    )}
                    ChipProps={{ color: "primary", variant: "outlined" }} />
                <TextField size="small" type="number" value={newSkill.years} onChange={e => setNewSkill({ ...newSkill, years: Number(e.target.value) })} label="Years of experience" fullWidth required />

            </Stack>
            {/* <Typography fontWeight={400} color="text.secondary" fontSize={{ xs: "0.9rem", md: "0.95rem" }} mt={3} mb={1}>
                Level of expertise*
            </Typography> */}
            {/* <Box display="flex" alignItems="center"> */}
            {/* <StyledRating size="large" max={3}
                value={newSkill.level}
                precision={1}
                getLabelText={(value) => (SkillExperienceLabels as any)[value]}
                onChange={(event, newValue) => {
                    setNewSkill({ ...newSkill, level: newValue || undefined as any });
                }}
                onChangeActive={(event, newHover) => {
                    setHover(newHover);
                }} */}
            {/* // icon={<Circle fontSize="inherit" />} */}
            {/* // emptyIcon={<Circle style={{ opacity: 0.55 }} fontSize="inherit" />} */}
            {/* /> */}
            {/* {(hover || newSkill.level) && <Box sx={{ ml: 2, whiteSpace: "nowrap" }}>{(SkillExperienceLabels as any)[hover || newSkill.level || 0]}</Box>} */}
            {/* </Box> */}
            <Box display="flex" justifyContent="center" my={4}>
                <Button onClick={() => { clearSkill(); setIsAddingSkills(false); }} color="inherit" sx={{ mr: 2 }}>Cancel</Button>
                <Button variant="contained" onClick={addSkill} disabled={isSavingSkills}>Submit</Button>
            </Box>
        </Box>
    </Dialog>;
}


export default AddSkillDialog;

